import { ArrowClockwise, Check, PaperPlaneTilt, Sparkle, X } from "@phosphor-icons/react";
import { Portal } from "radix-ui";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAiActionPreferences } from "~/features/documents/editor/aiActionPreferences";
import {
  type DrawerBounds,
  useSelectionActionsStore,
  visibleThoughts,
} from "~/features/documents/editor/selectionActionsStore";
import { drawerLayout } from "~/features/documents/editor/selectionDrawerLayout";
import { Button } from "~/foundation/components/Button";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";

/**
 * The results drawer — one action's run, from "thinking" to accepted.
 *
 * Four states, and the transitions between them are the product: **processing** streams the
 * model's reasoning at whatever volume the viewer chose, **result** offers the text with
 * Accept / Refine / Reject, **conversation** turns Refine into a chat that ends in a better
 * result, and **gated** says the AI provider is not configured — but only when the server
 * said so explicitly, because a false "configure your provider" wall in front of a working
 * AI is worse than no wall.
 *
 * Accept never touches text directly: the host applies through `applyAiResult`, which parses
 * the markdown with the editor's own parser and splices it. That indirection is what keeps an
 * accepted list from arriving as literal `- ` characters.
 */
export interface SelectionActionsDrawerProps {
  /** Applies the current result to the anchored range. Returns false when it could not. */
  onAccept: (result: string) => boolean;
  /** Sends a follow-up; the host owns the stream and pushes frames into the store. */
  onRefine: (message: string) => void;
  /** Cancels anything in flight and closes. */
  onDismiss: () => void;
  /** Whether a refinement is currently streaming. */
  refining: boolean;
}

export function SelectionActionsDrawer({
  onAccept,
  onRefine,
  onDismiss,
  refining,
}: SelectionActionsDrawerProps) {
  const { t, i18n } = useTranslation();
  const phase = useSelectionActionsStore((state) => state.phase);
  const action = useSelectionActionsStore((state) => state.action);
  const bounds = useSelectionActionsStore((state) => state.bounds);
  const thoughts = useSelectionActionsStore((state) => state.thoughts);
  const result = useSelectionActionsStore((state) => state.result);
  const turns = useSelectionActionsStore((state) => state.turns);
  const streaming = useSelectionActionsStore((state) => state.streaming);
  const error = useSelectionActionsStore((state) => state.error);
  const gateReason = useSelectionActionsStore((state) => state.gateReason);
  const startConversation = useSelectionActionsStore((state) => state.startConversation);

  const thoughtDisplay = useAiActionPreferences((state) => state.thoughtDisplay);
  const drawerPosition = useAiActionPreferences((state) => state.drawerPosition);

  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const thoughtsRef = useRef<HTMLDivElement | null>(null);

  const open = phase !== "idle";
  const hasResult = result.trim().length > 0;

  // The conversation input takes focus on entering that state. In an effect, never with the
  // `autoFocus` attribute — which the lint rules ban precisely because it fires before the
  // element is positioned and yanks the page.
  useEffect(() => {
    if (phase === "conversation") {
      inputRef.current?.focus();
    }
  }, [phase]);

  // The thought stream auto-scrolls while it grows, or a long trace scrolls away unread.
  useEffect(() => {
    const el = thoughtsRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [thoughts]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onDismiss();
        return;
      }
      // ⌘/Ctrl+Enter accepts — but only when there is something to accept.
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter" && hasResult) {
        event.preventDefault();
        onAccept(result);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, hasResult, onAccept, onDismiss, result]);

  if (!open) {
    return null;
  }

  const layout = drawerLayout(bounds as DrawerBounds | null, drawerPosition, {
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    isMobile: window.innerWidth < 640,
    rtl: i18n.dir() === "rtl",
  });

  const shown = visibleThoughts(thoughts, thoughtDisplay);

  return (
    <Portal.Root>
      <div
        role="dialog"
        aria-modal="false"
        aria-label={action?.title ?? t("AI action")}
        style={{
          position: "fixed",
          zIndex: 60,
          ...(layout.top === undefined ? {} : { top: layout.top }),
          ...(layout.bottom === undefined ? {} : { bottom: layout.bottom }),
          ...(layout.left === undefined ? {} : { left: layout.left }),
          ...(layout.right === undefined ? {} : { right: layout.right }),
          width: layout.width,
          maxHeight: layout.maxHeight,
        }}
        className={`flex flex-col gap-3 overflow-y-auto border border-divider bg-menu-bg p-4 shadow-menu ${
          layout.sheet ? "rounded-t-lg" : "rounded-md"
        }`}
      >
        <div className="flex items-center gap-2">
          <Sparkle size={16} aria-hidden className="text-accent" />
          <Text variant="small" className="font-medium text-text">
            {action?.title ?? t("AI action")}
          </Text>
          {phase === "processing" || refining ? <Spinner label={t("Working")} /> : null}
          <button
            type="button"
            aria-label={t("Close")}
            onClick={onDismiss}
            className="ms-auto rounded-sm p-1 text-text-tertiary hover:bg-hover-bg hover:text-text"
          >
            <X size={14} aria-hidden />
          </button>
        </div>

        {phase === "gated" ? (
          <div className="flex flex-col gap-2">
            <Text variant="small" className="text-text">
              {t("No AI provider is configured for this workspace.")}
            </Text>
            <Text variant="small" className="text-text-tertiary">
              {gateReason ?? t("An administrator can add one in AI settings.")}
            </Text>
            <Button variant="neutral" onClick={onDismiss}>
              {t("Dismiss")}
            </Button>
          </div>
        ) : null}

        {phase === "error" ? (
          <div className="flex flex-col gap-2">
            {/* The server's own sentence, never a paraphrase — a named thinker timeout and a
                bad request are different problems and must read differently. */}
            <Text variant="small" className="text-danger">
              {error ?? t("Something went wrong")}
            </Text>
            <Button variant="neutral" onClick={onDismiss}>
              {t("Dismiss")}
            </Button>
          </div>
        ) : null}

        {shown.length > 0 && phase !== "gated" && phase !== "error" ? (
          <div className="flex flex-col gap-1">
            <Text variant="small" className="text-text-tertiary">
              {t("AI Thinking")}
            </Text>
            <div
              ref={thoughtsRef}
              className="max-h-24 overflow-y-auto rounded-sm bg-bg-secondary p-2"
            >
              {shown.map((thought, index) => (
                <Text
                  // Thoughts arrive in order and are never reordered or removed, so the text
                  // plus its position is a stable identity — and an index alone is not a key.
                  key={`${index}-${thought.slice(0, 24)}`}
                  variant="small"
                  className="text-text-tertiary"
                >
                  {thought}
                </Text>
              ))}
            </div>
          </div>
        ) : null}

        {phase === "conversation" ? (
          <div className="flex flex-col gap-2">
            {turns.map((turn, index) => (
              <div
                key={`${index}-${turn.role}-${turn.content.slice(0, 24)}`}
                className={
                  turn.role === "user"
                    ? "self-end rounded-md bg-accent/10 px-2 py-1"
                    : "self-start rounded-md bg-bg-secondary px-2 py-1"
                }
              >
                <Text variant="small" className="whitespace-pre-wrap text-text">
                  {turn.content}
                </Text>
              </div>
            ))}
            {streaming ? (
              <div className="self-start rounded-md bg-bg-secondary px-2 py-1">
                <Text variant="small" className="whitespace-pre-wrap text-text-tertiary">
                  {streaming}
                </Text>
              </div>
            ) : null}
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                value={draft}
                aria-label={t("Type to refine…")}
                placeholder={t("Type to refine…")}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey && draft.trim()) {
                    event.preventDefault();
                    onRefine(draft.trim());
                    setDraft("");
                  }
                }}
                className="h-8 flex-1 rounded-sm border border-input-border bg-bg px-2 text-body text-text outline-none"
              />
              <button
                type="button"
                aria-label={t("Send")}
                disabled={!draft.trim() || refining}
                onClick={() => {
                  onRefine(draft.trim());
                  setDraft("");
                }}
                className="rounded-sm p-1.5 text-text-secondary hover:bg-hover-bg hover:text-text disabled:opacity-40"
              >
                <PaperPlaneTilt size={16} aria-hidden className="rtl:rotate-180" />
              </button>
            </div>
          </div>
        ) : null}

        {hasResult && phase !== "gated" && phase !== "error" ? (
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-sm bg-bg-secondary p-2 text-small text-text">
            {result}
          </pre>
        ) : null}

        {phase === "result" || phase === "conversation" ? (
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              icon={Check}
              disabled={!hasResult}
              onClick={() => onAccept(result)}
            >
              {phase === "conversation" ? t("Accept Result") : t("Accept")}
            </Button>
            {phase === "result" ? (
              <Button variant="neutral" icon={ArrowClockwise} onClick={startConversation}>
                {t("Refine")}
              </Button>
            ) : null}
            <Button variant="ghost" onClick={onDismiss}>
              {phase === "conversation" ? t("Cancel") : t("Reject")}
            </Button>
          </div>
        ) : null}
      </div>
    </Portal.Root>
  );
}
